// @core/stores/fiscalization/FiscalizationStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { fiscalizationApi } from '@core/api';
import type {
  CreateFiscalPolicyRequest,
  FiscalPolicy,
  ResolveFiscalContextRequest,
  ResolveFiscalContextResponse,
  UpdateFiscalPolicyRequest,
} from '@core/types/fiscalization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FiscalizationStore {
  // List state
  items: FiscalPolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FiscalPolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FiscalPolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FiscalPolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FiscalPolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create fiscal policy
   */
  async CreateFiscalPolicy(data: CreateFiscalPolicyRequest): Promise<FiscalPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.CreateFiscalPolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete fiscal policy
   */
  async DeleteFiscalPolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await fiscalizationApi.DeleteFiscalPolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get fiscal policy
   */
  async GetFiscalPolicy(policy_id: string): Promise<FiscalPolicy> {
    // Check cache first
    const cached = this.cache.get(policy_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.GetFiscalPolicy(policy_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get entity fiscal policy
   */
  async GetFiscalPolicyByEntity(entity_type: string, entity_id: string): Promise<FiscalPolicy> {
    // Check cache first
    const cached = this.cache.get(entity_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.GetFiscalPolicyByEntity(entity_type, entity_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List fiscal policies
   */
  async ListFiscalPolicies(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.ListFiscalPolicies(params);

      runInAction(() => {
        this.items = response.fiscal_policies?.policies ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve fiscal context
   */
  async ResolveFiscalContext(data: ResolveFiscalContextRequest): Promise<ResolveFiscalContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.ResolveFiscalContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update fiscal policy
   */
  async UpdateFiscalPolicy(policy_id: string, data: UpdateFiscalPolicyRequest): Promise<FiscalPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.UpdateFiscalPolicy(policy_id, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const fiscalizationStore = new FiscalizationStore();
